import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useCallback, useEffect, useState } from "react";

import {
  formatDateTime,
  formatInt,
  formatUsd,
} from "@kontextwerk/web-common";

import AnonymizationBadge from "../components/AnonymizationBadge";
import EstimationMethodBadge from "../components/EstimationMethodBadge";
import FilterBar from "../components/FilterBar";
import { fetchCostRecords } from "../lib/api";
import { defaultRange, type DateRange } from "../lib/dateRange";
import type { CostFilters, CostRecord } from "../lib/types";

const PAGE_SIZE = 50;

function buildColumns(): ColumnDef<CostRecord>[] {
  return [
    {
      accessorKey: "occurred_at",
      header: "Zeit",
      cell: (info) => (
        <span className="font-mono text-[11px]">
          {formatDateTime(info.getValue<string>())}
        </span>
      ),
    },
    {
      accessorKey: "cost_layer",
      header: "Layer",
      cell: (info) => (
        <span className="font-mono">{info.getValue<string>()}</span>
      ),
    },
    {
      accessorKey: "op_class",
      header: "op_class",
      cell: (info) => (
        <span className="font-mono">{info.getValue<string>()}</span>
      ),
    },
    { accessorKey: "pack_id", header: "Pack" },
    { accessorKey: "provider", header: "Provider" },
    {
      accessorKey: "model",
      header: "Model",
      cell: (info) => (
        <span className="text-[11px]">{info.getValue<string | null>() ?? "—"}</span>
      ),
    },
    {
      accessorKey: "input_tokens",
      header: "in",
      cell: (info) => formatInt(info.getValue<number | null>()),
    },
    {
      accessorKey: "output_tokens",
      header: "out",
      cell: (info) => formatInt(info.getValue<number | null>()),
    },
    {
      accessorKey: "total_cost_usd",
      header: "USD",
      cell: (info) => (
        <span className="font-mono text-right block">
          {formatUsd(info.getValue<number | null>())}
        </span>
      ),
    },
    {
      accessorKey: "cost_estimation_method",
      header: "Pricing",
      cell: (info) => (
        <EstimationMethodBadge method={info.getValue<string>()} />
      ),
    },
    {
      accessorKey: "payer",
      header: "Payer",
      cell: (info) => (
        <span className="text-[11px]">{info.getValue<string>()}</span>
      ),
    },
    {
      accessorKey: "anonymized",
      header: "anon",
      cell: (info) => <AnonymizationBadge anonymized={info.getValue<boolean>()} />,
    },
  ];
}

export default function RecordsRoute() {
  const [range, setRange] = useState<DateRange>(defaultRange(7));
  const [filters, setFilters] = useState<CostFilters>({});
  const [records, setRecords] = useState<CostRecord[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (nextCursor: number | null, reset: boolean) => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetchCostRecords(
          range.from,
          range.to,
          filters,
          nextCursor,
          PAGE_SIZE,
        );
        setRecords((prev) => (reset ? resp.records : [...prev, ...resp.records]));
        setCursor(resp.next_cursor);
        setHasMore(resp.next_cursor !== null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [range.from, range.to, filters],
  );

  useEffect(() => {
    loadPage(null, true);
  }, [loadPage]);

  const table = useReactTable({
    data: records,
    columns: buildColumns(),
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-medium">Records</h1>
        <span className="text-xs text-kw-text-faint">
          {records.length} Records geladen{hasMore ? " (mehr verfügbar)" : ""}
        </span>
      </header>
      <FilterBar
        range={range}
        onRangeChange={setRange}
        filters={filters}
        onFiltersChange={setFilters}
        showLayerFilter
      />
      {error ? (
        <div className="p-3 bg-red-900/30 border border-red-700/50 rounded text-sm text-red-300">
          {error}
        </div>
      ) : null}
      <div className="bg-kw-bg-elevated border border-kw-border-subtle rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-kw-bg-overlay text-kw-text-muted text-xs">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id} className="px-3 py-2 text-left font-medium">
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && !loading ? (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  className="px-3 py-8 text-center text-kw-text-faint"
                >
                  Keine Records für die aktuellen Filter.
                </td>
              </tr>
            ) : null}
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className="border-t border-kw-border-subtle hover:bg-kw-bg-overlay/30"
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-3 py-2 align-top">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-center">
        <button
          onClick={() => loadPage(cursor, false)}
          disabled={loading || !hasMore}
          className="px-4 py-2 rounded bg-kw-l3 text-white text-sm disabled:opacity-50"
        >
          {loading ? "Lade…" : hasMore ? "Weitere laden" : "Ende erreicht"}
        </button>
      </div>
    </div>
  );
}
